/**
 Button with attached string of visible attachments
 */
import {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import {toast} from "react-toastify";
import {Button} from "react-bootstrap";
import {buildStyles, CircularProgressbar} from "react-circular-progressbar";
import {areSetsEqual, fileKey, isImage, xhrUpload} from "../../lib/utils";
import { getConfig } from "../../lib/configMgr";
const { ExternalRESTAPI } = getConfig();

const FileAttachments = forwardRef(({
                           uploadURL = ExternalRESTAPI.Upload,
                           className,
                           value = [],
                           disabled = false,
                           onChange = (files) => {},
                           onFileUploadProgress = (file, progress) => {},
                           onFileUploadComplete = (file, response) => {},
                           onFileUploadError = (file, error) => {},
                           limit = null,
                           documentLimit = null,
                           documentSizeLimit = null,
                           imageLimit = null,
                           imageSizeLimit = null,
                           accept = null,
                           readOnly = false,
                           authData = {},
                           uploadFile = () => Promise.resolve(),
                           hideButton = false,
                         }, ref) => {

  const acceptableTypes = new Set(accept || [])
  const fileInputRef = useRef();
  useImperativeHandle(ref, () => ({
    click: () => fileInputRef.current?.click(),
  }), []);
  // Map of fileKey => uploadState
  // Handle both File objects and {file, error, errorMessage, uploadId} objects
  const [selectedFiles, setSelectedFiles] = useState((value || []).reduce((result, item) => {
    const file = item?.file || item;
    const error = item?.error;
    const errorMessage = item?.errorMessage;
    const uploadId = item?.uploadId;
    result[fileKey(file)] = {file, progress: 0, active: !readOnly && !disabled, error, errorMessage, uploadId}
    return result;
  }, {}));

  useEffect(() => {
    if (!value || value.length === 0) {
      setSelectedFiles({});
      return;
    }
    const local = new Set(Object.values(selectedFiles).map(s => fileKey(s.file)));
    const requested = new Set(value.map(item => {
      const file = item?.file || item;
      return fileKey(file);
    }));
    if (!areSetsEqual(local, requested)) {
      setSelectedFiles(value.reduce((result, item) => {
        const file = item?.file || item;
        const error = item?.error;
        const errorMessage = item?.errorMessage;
        const uploadId = item?.uploadId;
        const key = fileKey(file);

        // Preserve existing upload state if file already exists
        const existingState = selectedFiles[key];
        const shouldStartUpload = !readOnly && !disabled && !error && !uploadId && !existingState;

        result[key] = {
          file,
          progress: existingState?.progress || 0,
          // Keep active true until upload completes (has uploadId) or errors
          active: uploadId ? false : (existingState?.active ?? (!readOnly && !disabled)),
          error,
          errorMessage,
          uploadId
        };

        if (shouldStartUpload) {
          uploadFile(uploadURL, file, authData, handleError, handleProgress, handleComplete)
            .then(r => {});
        }
        return result;
      }, {}));
    }
    else {
      //console.log("Value unchanged", value, local, requested);
    }
  }, [value]);

  const sanitizedName = (s) => {
    // According to limitations specified in https://docs.aws.amazon.com/bedrock/latest/APIReference/API_runtime_DocumentBlock.html
    return s.replace(/[^-()[\]\s\w]/, " ").replace(/\s{2,}/, " ").substring(0, 200);
  };

  useEffect(() => {
    onChange(Object.values(selectedFiles).reduce((result, updateState) => {
      // Include uploadId (or error) with the file if available, so a failed
      // upload doesn't collapse back into an indistinguishable plain File.
      if (updateState.uploadId) {
        result.push({name: updateState.file.name, size: updateState.file.size, lastModified: updateState.file.lastModified, type: updateState.file.type, uploadId: updateState.uploadId});
      } else if (updateState.error) {
        result.push({name: updateState.file.name, size: updateState.file.size, lastModified: updateState.file.lastModified, type: updateState.file.type, error: updateState.error, errorMessage: updateState.errorMessage});
      } else {
        result.push(updateState.file);
      }
      return result;
    }, []));
  }, [selectedFiles]);

  const handleProgress = uploadState => {
    setSelectedFiles(files => {
      const updated = {...files};
      const key = fileKey(uploadState.file);
      // Preserve active state during progress updates
      updated[key] = {
        ...uploadState,
        active: uploadState.active ?? true  // Keep active true during upload
      };
      return updated;
    });
    onFileUploadProgress(uploadState.file, uploadState.progress);
  };

  const handleComplete = (file, response) => {
    // Extract upload ID from response (backend returns unique identifier)
    let uploadId = typeof response === 'string' ? response : response?.id || response?.uploadId;
    // Remove bounding quotes and whitespace that may have been added during transport
    if (typeof uploadId === 'string') {
      uploadId = uploadId.trim().replace(/^"(.*)"$/, '$1').replace(/^'(.*)'$/, '$1');
    }
    setSelectedFiles(files => {
      const updated = {...files};
      const key = fileKey(file);
      if (updated[key]) {
        updated[key] = {...updated[key], uploadId, active: false};
      }
      return updated;
    });
    onFileUploadComplete(file, response);
  };

  const handleError = uploadState => {
    setSelectedFiles(files => {
      const updated = {...files};
      updated[fileKey(uploadState.file)] = uploadState;
      return updated;
    })
    onFileUploadError(uploadState.file, uploadState.error, uploadState.response);
  };

  const handleAttach = (e) => {
    // NOTE: input returns a FileList, not an array
    const inputFiles = [...fileInputRef.current?.files || []];
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    //console.log(`Attach ${inputFiles.length} files`)
    const countImages = files => files.reduce((count, f) => isImage(f) ? count + 1 : count, 0);
    const warnings = new Set();
    //console.log("setSelectedFiles", inputFiles);
    setSelectedFiles(files => {
      //console.log("current files", files);
      const updated = {...files};
      inputFiles.forEach(file => {
        const extantKeys = new Set(Object.values(updated).map(s => fileKey(s.file)));
        const key = fileKey(file);
        const fileType = file.type;
        if (extantKeys.has(key)) {
          //console.log("Already have", file);
        }
        else {
          const nImages = countImages(Object.values(updated).map(s => s.file));
          //console.log(`image count: ${nImages}`);
          if (limit && extantKeys.size >= limit) {
            warnings.add(`You may attach no more than ${limit} files`);
          }
          if (isImage(file)) {
            if (imageLimit && nImages >= imageLimit) {
              warnings.add(`You may attach no more than ${imageLimit} images`);
              return;
            }
            if (imageSizeLimit && file.size > imageSizeLimit) {
              warnings.add(`Image file size may not exceed ${Math.floor(imageSizeLimit / (1024 * 1024))}Mb`)
              return;
            }
          }
          else {
            if (documentLimit && extantKeys.size - nImages >= documentLimit) {
              warnings.add(`You may attach no more than ${documentLimit} non-image documents`);
              return;
            }
            if (documentSizeLimit && file.size > documentSizeLimit) {
              warnings.add(`Document file size may not exceed ${Math.floor(documentSizeLimit / (1024 * 1024))}Mb`)
              return;
            }
          }
          if (accept && acceptableTypes.has(fileType)) {
            warnings.add(`Plexy may not be able to process the document '${file.name}' (type ${fileType})`);
          }
          updated[key] = {file, progress: 0, active: true};
          uploadFile(uploadURL, file, authData, handleError, handleProgress, handleComplete)
            .then(r => {});
        }
      });
      return updated;
    });
    Array.from(warnings).forEach(el => {
      toast.warn(el);
    });
  };

  const handleUnattach = file => {
    const key = fileKey(file);
    setSelectedFiles(files => {
      const updated = {...files};
      updated[key]?.xhr?.abort();
      delete updated[key];
      return updated;
    });
  }

  const inputProps = accept ? {accept} : {};
  //console.log("Current value", value, selectedFiles);
  return (
    <div className={`file-uploader${readOnly || disabled ? " disabled": ""}${className && ' ' + className || ''}`} >
      {!hideButton && (readOnly
       ? (<Button className={"glyphicon glyphicon-paperclip attach-button read-only"} disabled={true}></Button>)
       : (<Button
          className={"glyphicon glyphicon-paperclip attach-button"}
          onClick={() => fileInputRef.current?.click()}
          title={"Attach files"}
          disabled={disabled || (limit && Object.keys(selectedFiles).length >= limit)}
        />))}
      <input
        ref={fileInputRef}
        onChange={handleAttach}
        type="file"
        multiple={true}
        hidden
        {...inputProps}
      />
      {Object.entries(selectedFiles).map(([key, uploadState]) => {
        //console.log(`Render`, {...uploadState});
        const {file = null, error = null, errorMessage = null} = uploadState;
        const errorClass = uploadState?.status === 401
                           ? ' warning-message'
                           : error === 'not_found'
                             ? ' error-message attachment-not-found'
                             : uploadState?.error
                               ? ' error-message' : '';
        const titleText = error === 'not_found'
                          ? (errorMessage || "Attachment not found, please re-attach")
                          : uploadState?.error
                            ? "Attachment failed, remove and retry"
                            : "This file has been attached";
        return file && (
          <span key={key}
                className={`attached-file${errorClass}${uploadState?.active ? ' disabled': ''}`}
                title={titleText}
          >
            {!readOnly && !disabled && (
              <span
                key={"remove"}
                className={`glyphicon glyphicon-remove filter-box-token-delete delete-button`}
                title={"Remove this file from the upload list"}
                onClick={() => handleUnattach(file)}
              />)}
            {file.name}
            {uploadState?.active && (
              <div className={"circular-progress"}>
                <CircularProgressbar value={uploadState?.progress || 0} key={"progress"} strokeWidth={50} styles={buildStyles({strokeLinecap: "butt"})}/>
              </div>
            )}
          </span>
        );
      })}
    </div>
  )
});

export default FileAttachments;
